import type { Method9Form, Method9Observations, Method9Stage, Method9StageBoundary } from '../types/compliance.ts';
import type { Facility } from '../types/common.ts';

// Our current smallest interval is 3 minutes of readings taken every 15 seconds (3 * 60)/15 = 12, making this the bare
// minimum number of readings taken to ever comply with a Method 9 stage:
export const method9MinimumReadings = 12;

export function method9NumberOfReadingsRemaining(
  observations: Method9Observations,
  stageBoundaries: Method9StageBoundary[],
  stages: Method9Stage[],
  stageID: Method9Stage['stageID'],
): number {
  const stageBoundaryIndex = stageBoundaries.findIndex((boundary) => boundary.stageID === stageID);
  if (stageBoundaryIndex === -1) {
    console.warn('method9NumberOfReadingsRemaining: Tried to check a stage that has not yet been started.');
    return method9MinimumReadings;
  }

  const stageCurrent = stages.find((s) => s.stageID === stageID);
  if (!stageCurrent) {
    console.warn('method9NumberOfReadingsRemaining: Tried to check a stage that has no config.');
    return 0;
  }

  const allObservationsStartedAt = new Date(observations.observationsStarted).getTime();
  const stageStartedAt = new Date(stageBoundaries[stageBoundaryIndex].startedAt).getTime();
  const timeBetweenReadingsInSeconds = observations.timeBetweenReadingsInSeconds;

  const maybeNextStage = stageBoundaries[stageBoundaryIndex + 1];
  let maybeNextStageStartedAt = null as null | number;
  if (maybeNextStage) {
    maybeNextStageStartedAt = new Date(maybeNextStage.startedAt).getTime();
  }

  const percentagesWithValues = observations.opacityPercentages.filter((p, index) => {
    if (p === null) {
      return false;
    }

    const readAt = allObservationsStartedAt + 1000 * timeBetweenReadingsInSeconds * index;

    if (maybeNextStageStartedAt !== null && readAt >= maybeNextStageStartedAt) {
      return false;
    }

    return readAt >= stageStartedAt;
  }).length;

  const expectedCount = stageCurrent.rollingAverageWindowInSeconds / timeBetweenReadingsInSeconds;
  const needed1 = Math.max(0, expectedCount - percentagesWithValues);
  let needed2 = 0;

  if (stageCurrent.minimumDurationInSeconds) {
    const expectedCount2 = stageCurrent.minimumDurationInSeconds / timeBetweenReadingsInSeconds;
    needed2 = Math.max(0, expectedCount2 - percentagesWithValues);
  }

  return Math.max(needed1, needed2);
}

export function method9ObservationsAreComplete(
  observations: Method9Observations,
  stageBoundaries: Method9StageBoundary[],
  stages: Method9Stage[],
): { isComplete: true } | { isComplete: false; incompleteReason: string } {
  if (observations.opacityPercentages.length < method9MinimumReadings) {
    return {
      isComplete: false,
      incompleteReason: 'Too few data points have been recorded, and averages cannot be computed.',
    };
  }

  if (stageBoundaries.length < stages.length) {
    const stagesMissingData = new Set<Method9Stage['stageID']>(stages.map((s) => s.stageID));
    for (const boundary of stageBoundaries) {
      stagesMissingData.delete(boundary.stageID);
    }

    const names: string[] = [];
    for (const stage of stages) {
      if (stagesMissingData.has(stage.stageID)) {
        names.push(stage.name);
      }
    }

    return {
      isComplete: false,
      incompleteReason: `Not all stages have data recorded. Still need data for: ${names.join(', ')}`,
    };
  }

  for (const stage of stages) {
    const readingsRemaining = method9NumberOfReadingsRemaining(observations, stageBoundaries, stages, stage.stageID);
    if (readingsRemaining > 0) {
      return {
        isComplete: false,
        incompleteReason: `The stage "${stage.name}" needs ${readingsRemaining} more data point(s) in order to calculate averages needed for compliance.`,
      };
    }
  }

  return { isComplete: true };
}

export function method9FormCreateDefault(facility: Facility | undefined | null): Method9Form {
  return {
    companyName: facility?.name ?? '',
    streetAddress: facility?.address ?? '',
    city: facility?.city ?? '',
    state: facility?.state ?? '',
    zip: facility?.zip ?? '',
    keyContactPhone: '',
    sourceIdNumber: '',

    processEquipmentName: '',
    processEquipmentOperatingMode: '',
    controlEquipmentName: '',
    controlEquipmentOperatingMode: '',

    emissionPointDescription: '',
    heightAboveGroundLevel: '',
    heightAboveGroundLevelUnits: 'ft',
    heightRelativeToObserverStart: '',
    heightRelativeToObserverStartUnits: 'ft',
    heightRelativeToObserverEnd: '',
    heightRelativeToObserverEndUnits: 'ft',
    distanceFromObserverStart: '',
    distanceFromObserverStartUnits: 'ft',
    distanceFromObserverEnd: '',
    distanceFromObserverEndUnits: 'ft',
    directionFromObserverStart: '',
    directionFromObserverEnd: '',

    emissionsDescriptionStart: '',
    emissionsDescriptionEnd: '',
    emissionColorStart: '',
    emissionColorEnd: '',
    waterDropletPlumeIsAttached: null,
    pointOfOpacityStart: '',
    pointOfOpacityEnd: '',

    plumeBackgroundDescriptionStart: '',
    plumeBackgroundDescriptionEnd: '',
    backgroundColorStart: '',
    backgroundColorEnd: '',
    skyConditionsStart: '',
    skyConditionsEnd: '',
    windSpeedStart: '',
    windSpeedStartUnits: 'mph',
    windSpeedEnd: '',
    windSpeedEndUnits: 'mph',
    windDirectionStart: '',
    windDirectionEnd: '',
    ambientTempStart: '',
    ambientTempStartUnits: '°F',
    ambientTempEnd: '',
    ambientTempEndUnits: '°F',
    wetBulbTemp: '',
    wetBulbTempUnits: '°F',
    relativeHumidity: '',
    relativeHumidityUnits: '%',

    additionalInformation: '',
  };
}
